/* ============================================================
   trade-registration.js  –  multi-step form + validation
   ============================================================ */

class TradeRegistration {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 3;

    this.steps      = document.querySelectorAll('.trade-form-step');
    this.indicators = document.querySelectorAll('.trade-step');
    this.connectors = document.querySelectorAll('.trade-step-connector');

    this.nextButtons = document.querySelectorAll('.next-step');
    this.prevButtons = document.querySelectorAll('.prev-step');

    this.init();
  }

  init() {
    this.initToggleButtons();
    this.initInlineValidation();
    this.bindEvents();
    this.updateUI();
  }

  /* ── navigation ─────────────────────────────────────────── */

  bindEvents() {
    this.nextButtons.forEach(button => {
      button.addEventListener('click', () => this.nextStep());
    });

    this.prevButtons.forEach(button => {
      button.addEventListener('click', () => this.prevStep());
    });

    // Clickable step indicators — only back to completed steps
    this.indicators.forEach(ind => {
      ind.addEventListener('click', () => {
        const target = parseInt(ind.dataset.step);
        if (target < this.currentStep) {
          this.currentStep = target;
          this.updateUI();
        }
      });
    });

    // Final submit validation
    const form = document.querySelector('.trade-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        const step3 = form.querySelector('.trade-form-step[data-step="3"]');
        if (step3 && !this.validateStep3(step3)) {
          e.preventDefault();
          step3.querySelector('.trade-field-error, .trade-group-error')
               ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }

  nextStep() {
    if (this.currentStep >= this.totalSteps) return;

    const currentPanel = document.querySelector(`.trade-form-step[data-step="${this.currentStep}"]`);
    const validator = this.getValidator(this.currentStep);

    // ── BLOCK if validation fails ──
    if (validator && !validator.call(this, currentPanel)) {
      // scroll to first error
      currentPanel.querySelector('.trade-field-error, .trade-group-error')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.currentStep++;
    this.updateUI();
  }

  prevStep() {
    if (this.currentStep <= 1) return;
    this.currentStep--;
    this.updateUI();
  }

  updateUI() {
    // panels
    this.steps.forEach(step => step.classList.remove('active'));
    const activePanel = document.querySelector(`.trade-form-step[data-step="${this.currentStep}"]`);
    if (activePanel) {
      activePanel.classList.add('active');
      activePanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // indicators
    this.indicators.forEach(indicator => {
      const s    = parseInt(indicator.dataset.step);
      const span = indicator.querySelector('span');
      indicator.classList.remove('active', 'done');

      if (s < this.currentStep) {
        indicator.classList.add('done');
        span.innerHTML = '✔';
      } else if (s === this.currentStep) {
        indicator.classList.add('active');
        span.textContent = s;
      } else {
        span.textContent = s;
      }
    });

    // connectors
    this.connectors.forEach((connector, i) => {
      connector.classList.toggle('done', i < this.currentStep - 1);
    });
  }

  getValidator(stepNum) {
    return { 1: this.validateStep1, 2: this.validateStep2, 3: this.validateStep3 }[stepNum];
  }

  /* ── validators ─────────────────────────────────────────── */

  validateStep1(step) {
    let valid = true;
    this.clearAllErrors(step);

    const firstName = step.querySelector('[name="customer[first_name]"]');
    if (firstName && !firstName.value.trim()) {
      this.showError(firstName, 'First name is required.');
      valid = false;
    }

    const lastName = step.querySelector('[name="customer[last_name]"]');
    if (lastName && !lastName.value.trim()) {
      this.showError(lastName, 'Last name is required.');
      valid = false;
    }

    const email = step.querySelector('[name="customer[email]"]');
    if (email) {
      if (!email.value.trim()) {
        this.showError(email, 'Email is required.');
        valid = false;
      } else if (!this.isValidEmail(email.value)) {
        this.showError(email, 'Please enter a valid email address.');
        valid = false;
      }
    }

    const phone = step.querySelector('[name="customer[phone]"]');
    if (phone) {
      if (!phone.value.trim()) {
        this.showError(phone, 'Phone number is required.');
        valid = false;
      } else if (!this.isValidPhone(phone.value)) {
        this.showError(phone, 'Please enter a valid phone number.');
        valid = false;
      }
    }

    const role = step.querySelector('[name="customer[note][Role]"]');
    if (role && !role.value) {
      this.showError(role, 'Please select your role.');
      valid = false;
    }

    const years = step.querySelector('[name="customer[note][Years in Business]"]');
    if (years && !years.value) {
      this.showError(years, 'Please select years in business.');
      valid = false;
    }

    return valid;
  }

  validateStep2(step) {
    let valid = true;
    this.clearAllErrors(step);

    const company = step.querySelector('[name="customer[note][Company Name]"]');
    if (company && !company.value.trim()) {
      this.showError(company, 'Company name is required.');
      valid = false;
    }

    const website = step.querySelector('[name="customer[note][Website]"]');
    if (website && website.value.trim() && !this.isValidUrl(website.value)) {
      this.showError(website, 'Please enter a valid URL (e.g. https://example.com).');
      valid = false;
    }

    const country = step.querySelector('[name="customer[note][Country]"]');
    if (country && !country.value.trim()) {
      this.showError(country, 'Country is required.');
      valid = false;
    }

    const city = step.querySelector('[name="customer[note][City]"]');
    if (city && !city.value.trim()) {
      this.showError(city, 'City is required.');
      valid = false;
    }

    const projectGroup = step.querySelector('.checkbox-group:not(.step-3)');
    if (projectGroup) {
      const active = projectGroup.querySelectorAll('.button.active');
      if (!active.length) {
        this.showGroupError(projectGroup, 'Please select at least one project type.');
        valid = false;
      }
    }

    return valid;
  }

  validateStep3(step) {
    let valid = true;
    this.clearAllErrors(step);

    const interestsGroup = step.querySelector('.checkbox-group.three-col') ||
                           step.querySelector('[name="customer[note][Interests][]"]')?.closest('.checkbox-group');
    if (interestsGroup) {
      const checked = interestsGroup.querySelectorAll('input[type="checkbox"]:checked');
      if (!checked.length) {
        this.showGroupError(interestsGroup, 'Please select at least one area of interest.');
        valid = false;
      }
    }

    const collectionGroup = step.querySelector('.checkbox-group.step-3');
    if (collectionGroup) {
      const active = collectionGroup.querySelectorAll('.button.active');
      if (!active.length) {
        this.showGroupError(collectionGroup, 'Please select a preferred collection.');
        valid = false;
      }
    }

    const volume = step.querySelector('[name="customer[note][Estimated Annual Purchasing Volume]"]');
    if (volume && !volume.value) {
      this.showError(volume, 'Please select your estimated annual purchasing volume.');
      valid = false;
    }

    const radioGroup = step.querySelector('.radio-group');
    if (radioGroup) {
      const picked = radioGroup.querySelector('input[type="radio"]:checked');
      if (!picked) {
        this.showGroupError(radioGroup, 'Please answer the white-label question.');
        valid = false;
      }
    }

    const resourceGroups = step.querySelectorAll('.checkbox-group.three-col');
    const resourceGroup  = resourceGroups[resourceGroups.length - 1];
    if (resourceGroup) {
      const checked = resourceGroup.querySelectorAll('input[type="checkbox"]:checked');
      if (!checked.length) {
        this.showGroupError(resourceGroup, 'Please select at least one resource.');
        valid = false;
      }
    }

    return valid;
  }

  /* ── toggle buttons ─────────────────────────────────────── */

  initToggleButtons() {
    document.querySelectorAll('.checkbox-group .button').forEach(btn => {
      if (btn.classList.contains('next-step') || btn.classList.contains('prev-step')) return;
      btn.addEventListener('click', () => {
        btn.classList.toggle('active');
        const group = btn.closest('.checkbox-group');
        if (group) group.querySelector('.trade-group-error')?.remove();
      });
    });
  }

  /* ── inline validation ──────────────────────────────────── */

  initInlineValidation() {
    document.querySelectorAll('.trade-form-step').forEach(step => {

      step.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="url"]').forEach(input => {
        input.addEventListener('blur', () => {
          if (!input.value.trim()) {
            if (input.hasAttribute('required')) {
              this.showError(input, this.getRequiredMessage(input));
            } else {
              this.clearError(input);
            }
          } else {
            if (input.type === 'email' && !this.isValidEmail(input.value)) {
              this.showError(input, 'Please enter a valid email address.');
            } else if (input.type === 'tel' && !this.isValidPhone(input.value)) {
              this.showError(input, 'Please enter a valid phone number.');
            } else if (input.type === 'url' && !this.isValidUrl(input.value)) {
              this.showError(input, 'Please enter a valid URL.');
            } else {
              this.clearError(input);
            }
          }
        });

        input.addEventListener('input', () => {
          if (input.classList.contains('is-invalid') && input.value.trim()) {
            this.clearError(input);
          }
        });
      });

      step.querySelectorAll('select').forEach(sel => {
        sel.addEventListener('change', () => {
          if (sel.value) this.clearError(sel);
        });
      });
    });
  }

  /* ── helpers ─────────────────────────────────────────────── */

  showError(field, message) {
    this.clearError(field);
    field.classList.add('is-invalid');
    const err = document.createElement('span');
    err.className = 'trade-field-error';
    err.setAttribute('role', 'alert');
    err.textContent = message;
    const parent = field.closest('.trade-field') || field.parentNode;
    parent.appendChild(err);
  }

  clearError(field) {
    field.classList.remove('is-invalid');
    const parent = field.closest('.trade-field') || field.parentNode;
    parent.querySelector('.trade-field-error')?.remove();
  }

  clearAllErrors(step) {
    step.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));
    step.querySelectorAll('.trade-field-error').forEach(el => el.remove());
    step.querySelectorAll('.trade-group-error').forEach(el => el.remove());
  }

  showGroupError(container, message) {
    container.querySelector('.trade-group-error')?.remove();
    const err = document.createElement('span');
    err.className = 'trade-group-error';
    err.setAttribute('role', 'alert');
    err.textContent = message;
    container.appendChild(err);
  }

  getRequiredMessage(input) {
    const label = input.closest('.trade-field')?.querySelector('label');
    return `${label ? label.textContent.trim() : 'This field'} is required.`;
  }

  isValidEmail(val) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  }

  isValidPhone(val) {
    return /^[\d\s\+\-\(\)]{7,}$/.test(val.trim());
  }

  isValidUrl(val) {
    if (!val.trim()) return true;
    try {
      new URL(val.trim().startsWith('http') ? val.trim() : 'https://' + val.trim());
      return true;
    } catch {
      return false;
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new TradeRegistration();
});